import { getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import { withEnvironment } from "../extensions/with-environment"
import { Event, EventHandler, EventNames, FLAG_ACK, FLAG_REPLY } from "~proto"
import { observable } from "mobx"
import { RootStoreModel } from "~models/root-store"
import { Proto } from "~types/proto"

export const EventStoreModel = types
  .model("EventStore")
  .props({})
  .volatile(() => ({
    events: observable.array(),
    handlers: {},
  }))
  .views((self) => ({
    get items() {
      return self.events.slice().reverse()
    },
  }))
  .extend(withEnvironment)
  .actions((self) => {
    function clearEvents() {
      self.events.clear()
    }

    function onEvent<T>(event: Event<T>) {
      dispatch<T>(event)
    }

    function on<T>(event: EventNames, handler: EventHandler<T>) {
      self.handlers[event] = self.handlers[event] || []
      self.handlers[event].push(handler)
    }

    // apply event
    function apply<T>(event: Event<T>) {
      if (event.publish) {
        return _publish(event)
      }
      return dispatch(event)
    }

    function dispatch<T>(event: Event<T>) {
      if (__DEV__) {
        self.events.push(event)
      }
      ;(self.handlers[event.name] || []).forEach((f) => {
        try {
          f(event)
        } catch (e) {
          // console.error(e)
        }
      })
    }

    function _publish<T>(event: Event<T>) {
      if (__DEV__) self.events.push(event)
    }

    function publish<T>(
      evt: Event<T>,
      options?: {
        flags?: number
        onData?: (data) => void
        onError?: (err: Error) => void
        immediately?: boolean
      },
    ) {
      const { channelStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { flags = 0, onData, onError, immediately } = options || {}
      const proto = new Proto(evt.name, evt.data, flags)
      channelStore.send(proto, { onData, onError, immediately })
      if (__DEV__) self.events.push(evt)
    }

    function publishAsync<T>(
      evt: Event<T>,
      options?: {
        flags?: number
        immediately?: boolean
        timeout?: number
      },
    ) {
      const { channelStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { flags = 0, immediately, timeout } = options || {}

      return new Promise<any>((resolve, reject) => { // 返回值设为 any 以支持 data 返回
        const proto = new Proto(evt.name, evt.data, flags)

        // 核心修改：如果是 FLAG_REPLY，利用 send 的回调来 resolve Promise
        channelStore.send(proto, { 
          immediately,
          onData: (data) => {
            if (flags & FLAG_REPLY) resolve(data)
          },
          onError: (err) => {
            if (flags & FLAG_REPLY) reject(err)
          }
        })

        // 原有的 ACK 逻辑保持不变
        if (flags & FLAG_ACK) {
          channelStore.registerAckPromise(proto.id, resolve, reject, timeout)
        } else if (!(flags & FLAG_REPLY)) {
          // 既不是 ACK 也不是 REPLY 的普通发送，直接 resolve
          resolve(null)
        }

        if (__DEV__) self.events.push(evt)
      })
    }



    async function publicLogout() {
      const { authStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event(EventNames.USER_LOGOUT, {})

      try {
        await publishAsync(evt, { flags: FLAG_ACK })
        await authStore.logout()
      } catch (e) {
        // 是否强制登出
        await authStore.logout()
      }
    }

    // 注册事件处理函数
    function registerHandlers() {
      const {
        configStore,
        userStore,
        contactStore,
        messageStore,
        chatStore,
        friendStore,
        blackListStore,
        orderStore,
        onSyncFinished,
        onEventPacked,
        walletStore,
      } = getRoot<Instance<typeof RootStoreModel>>(self)

      on(EventNames.USER_PROFILE, userStore.onUserProfile) // 用户信息
      on(EventNames.USER_LIST, userStore.onUserList) // 用户列表
      on(EventNames.CONTACT_LIST, contactStore.onContactList) // 联系人列表
      on(EventNames.FRIEND_LIST, friendStore.onFriendList) // 新朋友列表
      on(EventNames.FRIEND_SENT_LIST, friendStore.onFriendSentList) // 好友发送列表
      on(EventNames.CHAT_LIST, chatStore.onChatList) // 聊天室列表
      on(EventNames.CHATS_LAST_MESSAGE_LIST, chatStore.onChatLastMessageList) // 聊天室最后一条消息
      on(EventNames.EVENT_PACKED, onEventPacked)
      on(EventNames.GAME_CHAT_UNREAD_SUMMARY, messageStore.onGameChatUnreadSummary)
      on(EventNames.SYNC_FINISHED, onSyncFinished)

      on(EventNames.USER_UPDATED, userStore.onUserUpdated)
      on(EventNames.FRIEND_CREATED, friendStore.onFriendCreated)
      on(EventNames.FRIEND_APPROVED, friendStore.onFriendApproved)
      on(EventNames.FRIEND_REJECTED, friendStore.onFriendRejected)
      on(EventNames.FRIEND_DELETED, friendStore.onFriendDeleted)
      on(EventNames.CONTACT_CREATED, contactStore.onContactCreated)
      on(EventNames.CONTACT_UPDATED, contactStore.onContactUpdated)
      on(EventNames.CONTACT_DELETED, contactStore.onContactDeleted)

      on(EventNames.MESSAGE_CREATED, messageStore.onMessageCreated)
      on(EventNames.MESSAGE_DELETED, messageStore.onMessageDeleted)
      on(EventNames.MESSAGE_STATUS_UPDATED, messageStore.onMessageStatusUpdated)
      on(EventNames.MESSAGE_ORDER_UPDATED, messageStore.onMessageUpdated)
      on(EventNames.MESSAGE_BATCH_STATUS_UPDATED, messageStore.onMessageBatchStatusUpdated)
      on(EventNames.MESSAGE_BATCH_READED, messageStore.onMessageBatchReaded)
      on(EventNames.MESSAGE_BATCH_DELETED, messageStore.onMessageBatchDeleted)

      on(EventNames.CHAT_UPDATED, chatStore.onChatUpdated)
      on(EventNames.USER_ACTION, chatStore.onUserAction)
      on(EventNames.CHAT_CREATED, chatStore.onChatCreated)
      on(EventNames.CHAT_REDPACKET_RECEIVED, chatStore.onChatRedPacketReceive)
      on(EventNames.CHAT_MEMBER_ADDED, chatStore.onChatMemberAdded)
      on(EventNames.CHAT_MEMBER_REMOVED, chatStore.onChatMemberRemoved)
      on(EventNames.CHAT_DELETED, chatStore.onChatDeleted)
      on(EventNames.CHAT_MEMBER_LEFT, chatStore.onChatMemberLeft)
      on(EventNames.MUTED, chatStore.onChatMuted)
      on(EventNames.USER_LAST_SEEN_LIST, userStore.onUserLastSeenList)
      on(EventNames.USER_LAST_SEEN, userStore.onUserLastSeen)
      on(EventNames.CHAT_BURNING_SWITCHED, chatStore.onChatBurningSwitched)
      on(EventNames.CHAT_MEMBER_PERMED, chatStore.onMemberRoleUpdated)
      on(EventNames.CHAT_SETTING_INFO_DATA, chatStore.onChannelChatInfo)
      on(EventNames.CHAT_MESSAGE_PINNED, chatStore.onChatMessagePinned)

      on(EventNames.BLACKLIST_LIST, blackListStore.onBlacklistList)
      on(EventNames.BLACKLIST_ADDED, blackListStore.onBlacklistAdded)
      on(EventNames.BLACKLIST_REMOVED, blackListStore.onBlacklistRemoved)

      on(EventNames.PENDING_ORDER_CREATED, orderStore.onOrderCreated)
      on(EventNames.PENDING_ORDER_UPDATED, orderStore.onOrderUpdated)
      on(EventNames.PENDING_ORDER_CANCELLED, orderStore.onOrderDeleted)
      on(EventNames.TRADING_ROOM_CREATED, orderStore.onTradingRoomCreated)

      on(EventNames.SETTINGS_INFO, configStore.onSettingInfo)
      on(EventNames.WALLET_SWITCHED, configStore.onWalletSwitched)
      on(EventNames.ORDER_PROPOSAL_CHANGED, configStore.onOrderProposalChanged)
      on(EventNames.WALLET_BALANCE_CHANGED, walletStore.onWalletBalanceChanged)
      on(EventNames.EXCHANGE_ORDER_SUCCEEDED, walletStore.onExchageOrcerSucceeded)
      on(EventNames.CHAT_MEMBER_PERM_UPDATE, walletStore.getWalletInfo)
      on(EventNames.CHAT_ANNOUNCEMENTED, chatStore.onChatAnnouncemented)
      on(EventNames.MESSAGE_SELF_DESTRUCT_DELETED, messageStore.onMessageBatchDeleted)

      on(EventNames.USER_DISABLED, publicLogout)
      on(EventNames.USER_INACTIVED, publicLogout)
    }

    function afterCreate() {
      registerHandlers()
    }

    return { on, dispatch, afterCreate, publish, apply, onEvent, clearEvents, publicLogout, publishAsync }
  })

type EventStoreType = Instance<typeof EventStoreModel>
export interface EventStore extends EventStoreType {}
type EventStoreSnapshotType = SnapshotOut<typeof EventStoreModel>
export interface EventStoreSnapshot extends EventStoreSnapshotType {}
export const createEventStoreDefaultModel = () => types.optional(EventStoreModel, {})
